import { Fragment, type ReactNode } from "react";
import { Link } from "react-router";

/**
 * The little Markdown AbiChat shows: paragraphs, bullet and numbered lists, **bold**, and links.
 * No HTML is ever produced from the text. The model's links may only go to app paths (it can't
 * send anyone to a site it made up); reviewed FAQ text may also link to https sites.
 */
export type LinkPolicy = "app" | "app-and-web";

export function safeHref(
  raw: string,
  policy: LinkPolicy,
): { href: string; internal: boolean } | null {
  const href = raw.trim();
  if (href.startsWith("/app") && !href.startsWith("//")) return { href, internal: true };
  if (policy === "app-and-web" && /^https:\/\//i.test(href)) return { href, internal: false };
  return null;
}

const INLINE = /\*\*([^*\n]+)\*\*|\[([^\]\n]+)\]\(([^)\s]+)\)/g;

function inline(text: string, policy: LinkPolicy, onNavigate?: () => void): ReactNode[] {
  const out: ReactNode[] = [];
  let at = 0;
  for (const m of text.matchAll(INLINE)) {
    const start = m.index ?? 0;
    if (start > at) out.push(text.slice(at, start));
    if (m[1] !== undefined) {
      out.push(<b key={start}>{m[1]}</b>);
    } else {
      const target = safeHref(m[3], policy);
      if (!target) out.push(m[2]);
      else if (target.internal)
        out.push(
          <Link key={start} to={target.href} onClick={onNavigate}>
            {m[2]}
          </Link>,
        );
      else
        out.push(
          <a key={start} href={target.href} target="_blank" rel="noopener noreferrer">
            {m[2]}
          </a>,
        );
    }
    at = start + m[0].length;
  }
  if (at < text.length) out.push(text.slice(at));
  return out;
}

const BULLET = /^\s*(?:[-*•]|\d+[.)])\s+/;

export function Markdown({
  text,
  policy = "app",
  onNavigate,
}: {
  text: string;
  policy?: LinkPolicy;
  onNavigate?: () => void;
}) {
  // Plain hyphens: some fonts draw U+2010/U+2011 (models like them) like a strike-through.
  const clean = text.replace(/\r/g, "").replace(/[\u2010\u2011]/g, "-");
  // Paragraphs and lists: a run of list lines is a list, even inside a paragraph ("Try: - a - b").
  const groups: { list: boolean; lines: string[] }[] = [];
  for (const block of clean.split(/\n{2,}/)) {
    let current: { list: boolean; lines: string[] } | undefined;
    for (const line of block.split("\n").filter((l) => l.trim())) {
      const list = BULLET.test(line);
      if (!current || current.list !== list) {
        current = { list, lines: [] };
        groups.push(current);
      }
      current.lines.push(line);
    }
  }
  return (
    <>
      {groups.map((g, i) => {
        if (g.list) {
          const ordered = /^\s*\d/.test(g.lines[0]);
          const items = g.lines.map((l, j) => (
            <li key={j}>{inline(l.replace(BULLET, ""), policy, onNavigate)}</li>
          ));
          return ordered ? <ol key={i}>{items}</ol> : <ul key={i}>{items}</ul>;
        }
        return (
          <p key={i}>
            {g.lines.map((l, j) => (
              <Fragment key={j}>
                {j > 0 && <br />}
                {inline(l.replace(/^#+\s*/, ""), policy, onNavigate)}
              </Fragment>
            ))}
          </p>
        );
      })}
    </>
  );
}
